import * as PIXI from "pixi.js";

/**
 * Thai line breaks (Loc QA, 2026-10-28: start page captions "overlapping").
 *
 * Thai writes no spaces between words. PIXI.Text wraps only at spaces, so a
 * Thai caption was ONE unbreakable word as wide as both chests, running into
 * its neighbour. Every Thai string gets an invisible ZERO-WIDTH SPACE between
 * its words — the browser's own Thai dictionary (Intl.Segmenter) finds them —
 * and PIXI is told that character is a place it may break. So Thai wraps
 * between words, never inside one. The DOM breaks at it as well.
 *
 * Applied once, to every translated string (i18n.js postProcess). Only text
 * that holds Thai letters is touched; every other language is unchanged. A
 * browser without Intl.Segmenter keeps the text as it was.
 */
const ZERO_WIDTH_SPACE = "​";
const THAI = /[฀-๿]/;

const segmenter =
  typeof Intl !== "undefined" && typeof Intl.Segmenter === "function"
    ? new Intl.Segmenter("th", { granularity: "word" })
    : null;

// PIXI's breaking characters (TextMetrics._breakingSpaces) — once.
const breakingSpaces = PIXI.TextMetrics?._breakingSpaces;
if (Array.isArray(breakingSpaces) && !breakingSpaces.includes(0x200b)) {
  breakingSpaces.push(0x200b);
}

/** `text` with a zero-width space between every two Thai words. */
export const addThaiLineBreaks = (text) => {
  if (!segmenter || typeof text !== "string" || !THAI.test(text)) return text;
  let out = "";
  let prevWordLike = false;
  for (const { segment, isWordLike } of segmenter.segment(text)) {
    // Between two WORDS only — never before punctuation or after a space.
    if (prevWordLike && isWordLike) out += ZERO_WIDTH_SPACE;
    out += segment;
    prevWordLike = isWordLike;
  }
  return out;
};

/** i18next post-processor: every translated string, see above. */
export const thaiLineBreaksPostProcessor = {
  type: "postProcessor",
  name: "thaiLineBreaks",
  process: (value) => addThaiLineBreaks(value),
};
